/**
 * @fileoverview WhenToTravelSection.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FadeUp } from '../../utils/motion'
import { apiFetch } from '../../utils/api'
import { useTranslation } from 'react-i18next'

// Simple helper to pick a color based on expense rate 1-5
const getExpenseColor = (rate) => {
    if (rate <= 1.5) return 'text-emerald-500'
    if (rate <= 2.5) return 'text-lime-500'
    if (rate <= 3.5) return 'text-yellow-500'
    if (rate <= 4.5) return 'text-orange-500'
    return 'text-red-500'
}

// getExpenseText is moved inside the component to use the translation hook

const getWeatherIconClass = (text) => {
    if (!text) return 'fi-br-cloud';
    const t = text.toLowerCase();
    if (t.includes('sun') || t.includes('clear')) return 'fi-br-sun text-yellow-500';
    if (t.includes('partly')) return 'fi-br-cloud-sun text-yellow-400';
    if (t.includes('rain') || t.includes('drizzle')) return 'fi-br-cloud-rain text-blue-500';
    if (t.includes('heavy rain') || t.includes('storm')) return 'fi-br-cloud-showers-heavy text-blue-600';
    if (t.includes('wind') || t.includes('breeze')) return 'fi-br-wind text-gray-400';
    if (t.includes('snow')) return 'fi-br-snowflake text-sky-400';
    return 'fi-br-cloud text-gray-500';
}

function SkeletonLoader() {
    return (
        <div className="w-full animate-pulse flex flex-col gap-8">
            <div className="flex flex-wrap justify-center gap-2">
                {[...Array(12)].map((_, i) => <div key={i} className="h-10 w-24 bg-gray-200 rounded-full"></div>)}
            </div>
            <div className="flex flex-col lg:flex-row gap-8 bg-white p-6 rounded-3xl shadow-sm">
                <div className="w-full lg:w-1/3 flex flex-col gap-6 border-r border-gray-100 pr-0 lg:pr-6">
                    <div className="h-16 w-16 bg-gray-200 rounded-full mb-2"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-4 w-full bg-gray-200 rounded"></div>
                    <div className="h-4 w-5/6 bg-gray-200 rounded"></div>
                    <div className="h-4 w-4/6 bg-gray-200 rounded"></div>
                </div>
                <div className="w-full lg:w-2/3 flex flex-col justify-center">
                    <div className="h-48 w-full bg-gray-200 rounded-2xl"></div>
                </div>
            </div>
        </div>
    )
}

function WhenToTravelSection() {
    const [monthsData, setMonthsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toLocaleString('default', { month: 'short' })); // e.g. "Aug"
    const { t } = useTranslation();

    const getExpenseText = (rate) => {
        if (rate <= 1.5) return t('when_to_travel.expense_levels.budget');
        if (rate <= 2.5) return t('when_to_travel.expense_levels.affordable');
        if (rate <= 3.5) return t('when_to_travel.expense_levels.moderate');
        if (rate <= 4.5) return t('when_to_travel.expense_levels.expensive');
        return t('when_to_travel.expense_levels.premium');
    }

    useEffect(() => {
        apiFetch('/when-to-travel')
            .then(res => {
                if (res && Array.isArray(res)) {
                    setMonthsData(res);
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    // Ensure months are ordered correctly
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    
    // Fallback static data if API is empty for a month
    const currentData = monthsData.find(m => m.month === selectedMonth) || {
        month: selectedMonth,
        weatherText: t('when_to_travel.weather_sunny'),
        description: t('when_to_travel.default_desc'),
        expenseRate: 3,
        dominantExperience: null
    };

    return (
        <div className='w-full px-[5%] md:px-[10%] py-16 bg-gray-50 flex flex-col gap-8 overflow-hidden'>
            <FadeUp className='flex flex-col items-center gap-2 text-center max-w-xl mx-auto'>
                <span className='text-KITH-primary font-bold tracking-widest uppercase text-xs'>{t('when_to_travel.subtitle')}</span>
                <h2 className='text-xl md:text-3xl font-bold text-gray-800 leading-tight'>
                    {t('when_to_travel.title')}
                </h2>
                <p className='text-gray-400 text-xs md:text-sm'>
                    {t('when_to_travel.desc')}
                </p>
            </FadeUp>

                <div className='flex flex-col gap-8'>
                    {/* Month Selector */}
                    <div className='flex flex-nowrap overflow-x-auto gap-2 md:justify-center pb-2 no-scrollbar'>
                        {MONTHS.map((m) => (
                            <button
                                key={m}
                                onClick={() => setSelectedMonth(m)}
                                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap shrink-0 ${
                                    selectedMonth === m
                                        ? 'bg-KITH-primary text-white shadow-md'
                                        : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800 border border-gray-200'
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>

                    <AnimatePresence mode='wait'>
                            <motion.div
                                key={selectedMonth}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className='flex flex-col lg:flex-row bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden lg:h-[60vh] lg:max-w-5xl lg:mx-auto w-full'
                            >
                                {/* Weather & Info Panel */}
                                <div className='w-full lg:w-[40%] p-6 md:p-8 flex flex-col gap-5 border-b lg:border-b-0 lg:border-r border-gray-100 bg-white z-10'>
                                    <div className='flex items-center gap-4'>
                                        {/* Weather Icon Box */}
                                        <div className='w-14 h-14 rounded-2xl bg-slate-50 flex flex-col items-center justify-center border border-slate-100 shrink-0 shadow-sm'>
                                            <i className={`text-2xl ${getWeatherIconClass(currentData.weatherText)}`}></i>
                                        </div>
                                        <div>
                                            <h3 className='text-xl font-bold text-gray-800'>{selectedMonth}</h3>
                                            <p className='text-xs text-gray-500 font-medium'>{currentData.weatherText || t('when_to_travel.weather_sunny')}</p>
                                        </div>
                                    </div>
                                    
                                    <p className='text-gray-600 text-sm leading-relaxed text-justify line-clamp-4'>
                                        {currentData.description}
                                    </p>

                                {/* Expense Rate Bar */}
                                <div className='mt-auto pt-6 border-t border-gray-100'>
                                    <div className='flex justify-between items-center mb-2'>
                                        <span className='text-xs font-bold text-gray-400 uppercase tracking-wider'>{t('when_to_travel.expense_rate')}</span>
                                        <span className={`text-xs font-bold ${getExpenseColor(currentData.expenseRate || 3)}`}>
                                            {getExpenseText(currentData.expenseRate || 3)}
                                        </span>
                                    </div>
                                    <div className='flex gap-1'>
                                        {[...Array(5)].map((_, i) => (
                                            <span 
                                                key={i} 
                                                className={`text-lg font-bold ${i < Math.round(currentData.expenseRate || 3) ? getExpenseColor(currentData.expenseRate || 3) : 'text-gray-200'}`}
                                            >
                                                $
                                            </span>
                                        ))}
                                    </div>
                                    <div className='mt-4'>
                                        <Link to={`/when-to-visit?month=${selectedMonth.toLowerCase()}`} className='inline-flex items-center gap-2 text-KITH-primary font-bold hover:underline text-sm'>
                                            {t('when_to_travel.more_in')} {selectedMonth} <span className='text-lg'>→</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Animated Dominant Experience Slide */}
                            <div className='w-full lg:w-[60%] relative h-64 lg:h-auto bg-gray-100 overflow-hidden'>
                                <AnimatePresence mode='wait'>
                                    {currentData.dominantExperience ? (
                                        <motion.div
                                            key={currentData.dominantExperience._id}
                                            initial={{ opacity: 0, scale: 1.05 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.6 }}
                                            className='absolute inset-0'
                                        >
                                            <img 
                                                src={currentData.dominantExperience.image?.h || currentData.dominantExperience.image?.l} 
                                                alt={currentData.dominantExperience.title}
                                                className='w-full h-full object-cover'
                                            />
                                            <div className='absolute inset-0 bg-gradient-to-t from-black/80 text-left via-black/20 to-transparent flex flex-col justify-end p-6 md:p-8'>
                                                <span className='text-white font-medium text-xs uppercase tracking-wider mb-1'>{t('when_to_travel.featured_exp')}</span>
                                                <h3 className='text-white text-xl md:text-3xl font-bold'>{currentData.dominantExperience.title}</h3>
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div 
                                            key="empty"
                                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                            className='absolute inset-0 flex items-center justify-center'
                                        >
                                            <img src="https://images.unsplash.com/photo-1539268672051-fb81726a8f15?q=80&w=2000" className='absolute inset-0 w-full h-full object-cover' alt="Sri lanka"/>
                                            <div className='absolute inset-0 bg-black/40'></div>
                                            <span className='text-white/80 font-medium z-10'>{t('when_to_travel.explore_sl')}</span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
        </div>
    )
}

export default WhenToTravelSection
